import React, { Suspense, lazy } from 'react';
import { LogPanel } from '../logs/LogPanel';
import { ShortcutsCard } from '../preview/ShortcutsCard';
import { StepsCard } from '../preview/StepsCard';
import { LogEntry, LogLocation } from '../../types';
import { GitCommit } from '../../hooks/useGit';
import { clamp } from '../../utils';
import { usePreviewContext, useLogs, useUI, useEditorState } from '../../contexts';

const PreviewPanel = lazy(() => import('../preview/PreviewPanel').then(m => ({ default: m.PreviewPanel })));

type PreviewSectionProps = {
  dropProps: {
    onDragOver: (e: React.DragEvent) => void;
    onDrop: (e: React.DragEvent) => void;
  };
  dragHandleProps: {
    draggable: boolean;
    onDragStart: () => void;
    onDragEnd: () => void;
  };
  className: string;
  style: React.CSSProperties;
  // Preview (computed values not from context)
  previewHtml: string;
  previewError: string | null;
  previewSize: { width: number; height: number };
  onRestart: () => void;
  onToggleSandbox: () => void;
  // Log panel
  hasTaskEndpoint: boolean;
  diffLines: { type: 'add' | 'del' | 'equal'; text: string; left?: number; right?: number }[];
  activePath: string | null;
  commits: {
    id: string;
    message: string;
    author: string;
    date: string;
    summary: string;
    diff: string[];
  }[];
  // New git commits
  gitCommits?: GitCommit[];
  isLoadingGitCommits?: boolean;
  onFetchGitCommits?: (count?: number) => Promise<GitCommit[]>;
  onLint: () => void;
  onTest: () => void;
  onBuild: () => void;
  getLocationFromLog: (log: LogEntry) => LogLocation | null;
  onNavigateLocation: (loc: LogLocation) => void;
};

export const PreviewSection = React.memo(function PreviewSection({
  dropProps,
  dragHandleProps,
  className,
  style,
  previewHtml,
  previewError,
  previewSize,
  onRestart,
  onToggleSandbox,
  hasTaskEndpoint,
  diffLines,
  activePath,
  commits,
  gitCommits,
  isLoadingGitCommits,
  onFetchGitCommits,
  onLint,
  onTest,
  onBuild,
  getLocationFromLog,
  onNavigateLocation,
}: PreviewSectionProps) {
  const { hotReloadEnabled, toggleHotReload, sandboxedPreview, previewVersion, previewDevice, setPreviewDevice, previewOrientation, toggleOrientation, customPreviewWidth, setCustomPreviewWidth, touchMode, toggleTouchMode, showDeviceFrame, toggleDeviceFrame } = usePreviewContext();
  const { taskFilteredLogs, logFilter, setLogFilter, taskTab, setTaskTab, clearTaskLogs } = useLogs();
  const { logView, setLogView, taskRunning } = useUI();
  const { selectedCommitId, setSelectedCommitId } = useEditorState();
  return (
    <aside
      {...dropProps}
      className={className}
      style={style}
      aria-label="Preview and output panel"
    >
      <div className="panel-header">
        <div>
          <p className="eyebrow">Preview & Logs</p>
          <h2>Output</h2>
        </div>
        <div className="panel-header-actions">
          <div className="panel-drag-handle" {...dragHandleProps} title="Panelleri sürükle">
            ⠿
          </div>
        </div>
      </div>
      <Suspense fallback={<div className="panel-loading">Loading preview...</div>}>
        <PreviewPanel
          hotReloadEnabled={hotReloadEnabled}
          sandboxedPreview={sandboxedPreview}
          previewVersion={previewVersion}
          previewHtml={previewHtml}
          previewError={previewError}
          previewSize={previewSize}
          previewDevice={previewDevice}
          previewOrientation={previewOrientation}
          customPreviewWidth={customPreviewWidth}
          onToggleHotReload={toggleHotReload}
          onRestart={onRestart}
          onToggleSandbox={onToggleSandbox}
          onChangeDevice={setPreviewDevice}
          onToggleOrientation={toggleOrientation}
          onChangeCustomWidth={(value) => setCustomPreviewWidth(clamp(value, 320, 1600))}
          touchMode={touchMode}
          onToggleTouchMode={toggleTouchMode}
          showDeviceFrame={showDeviceFrame}
          onToggleDeviceFrame={toggleDeviceFrame}
        />
      </Suspense>

      <LogPanel
        filteredLogs={taskFilteredLogs}
        logFilter={logFilter}
        taskTab={taskTab}
        logView={logView}
        taskRunning={taskRunning}
        hasTaskEndpoint={hasTaskEndpoint}
        diffLines={diffLines}
        activePath={activePath}
        commits={commits}
        selectedCommitId={selectedCommitId}
        gitCommits={gitCommits}
        isLoadingGitCommits={isLoadingGitCommits}
        onFetchGitCommits={onFetchGitCommits}
        onChangeFilter={setLogFilter}
        onChangeTaskTab={setTaskTab}
        onClearTaskLogs={clearTaskLogs}
        onChangeLogView={setLogView}
        onLint={onLint}
        onTest={onTest}
        onBuild={onBuild}
        getLocationFromLog={getLocationFromLog}
        onNavigateLocation={onNavigateLocation}
        onSelectCommit={setSelectedCommitId}
      />

      <ShortcutsCard />
      <StepsCard />
    </aside>
  );
});
